/**
 * Access Hook.
 * 
 * This custom hook is used for shared access of storages.
 * If given a storageId, it will be be used for storage specific access,
 * otherwise, the users received access invitations.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AccessInvitation, AccessRole, SortDirection, FormStatus, AccessInvitationRead } from '@/types';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackInvitationResponse } from '@/services/analytics';
import useAuth from './useAuth';
import { clearStoragesCache } from './useStorages';
import { clearTargetStorage, getTargetStorageDocId } from '@/utils/targetStorage';
import { 
    getStorageAccessInvitationDocs, 
    getUserAccessInvitationDocs,
    addAccessInvitationDocs,
    respondToAccessInvitation,
    deleteAccessInvitationDoc
} from '@/services/firebaseApi';

export enum AccessInvitationsSortBy {
    ROLE = 'role',
    EMAIL = 'email',
    STATUS = 'status',
}

interface useAccessProps {
    storageId?: string
    refetchInterval?: number | false
}

const useAccess = ({ storageId, refetchInterval = false }: useAccessProps) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(AccessInvitationsSortBy.ROLE);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');

    const { getUserId } = useAuth();

    // Storage access invitations query
    const {
        data: storageInvitations,
        isLoading: storageInvitationsLoading,
        error: storageInvitationsError,
        refetch: refetchStorageInvitations
    } = useQuery({
        queryKey: ['storageAccessInvitations', storageId],
        queryFn: () => getStorageAccessInvitationDocs({
            storageId: storageId!,
            sortBy,
            sortDirection
        }),
        enabled: !!(getUserId() && storageId),
        staleTime: 3 * 60 * 1000, // Access invitations don't change very often
        gcTime: 10 * 60 * 1000,
        refetchInterval,
    });

    // User access invitations query
    const {
        data: userInvitations,
        isLoading: userInvitationsLoading,
        error: userInvitationsError,
        refetch: refetchUserInvitations
    } = useQuery({
        queryKey: ['userAccessInvitations', storageId],
        queryFn: () => getUserAccessInvitationDocs(storageId),
        enabled: !!(getUserId() && !storageId),
        staleTime: 3 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchInterval,
    });

    // Apply client-side filtering
    const filteredStorageInvitations = useMemo(() => {
        if (!storageInvitations) return [];
        let filtered = storageInvitations.filter(
            (invitation) => invitation.role !== AccessRole.OWNER
        );
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((invitation) =>
                invitation.invitation.email.toLowerCase().includes(lower) ||
                invitation.role.toLowerCase().includes(lower) ||
                invitation.status.toLowerCase().includes(lower)
            );
        }
        return filtered;
    }, [storageInvitations, filterQuery]);

    const filteredUserInvitations = useMemo(() => {
        if (!userInvitations) return [];
        
        let filtered = userInvitations.filter(
            (invitation) => invitation.role !== AccessRole.OWNER
        );

        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((invitation) =>
                invitation.invitation.email.toLowerCase().includes(lower) ||
                invitation.role.toLowerCase().includes(lower) ||
                invitation.status.toLowerCase().includes(lower) ||
                (invitation.invitation.message && invitation.invitation.message.toLowerCase().includes(lower)) ||
                (invitation.storage?.storageName && invitation.storage.storageName.toLowerCase().includes(lower))
            );
        }

        return filtered;
    }, [userInvitations, filterQuery]);

    // Mutations
    const createAccessInvitationMutation = useMutation({
        mutationFn: addAccessInvitationDocs,
        onSuccess: (_, variables) => {
            trackCreate('access_invitation');
            logger.info('Access invitation created successfully');
            queryClient.invalidateQueries({ queryKey: ['storageAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['userAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['accessInvitations'] });
            if (variables?.storageId) {
                queryClient.invalidateQueries({ queryKey: ['storage', variables.storageId] });
            }
        },
        onError: (error) => {
            logger.error('Failed to create access invitation:', error, 'useAccess');
        }
    });

    const respondToInvitationMutation = useMutation({
        mutationFn: respondToAccessInvitation,
        onSuccess: (_, variables) => {
            trackInvitationResponse(variables.accept);
            logger.info('Responded to access invitation successfully');
            queryClient.invalidateQueries({ queryKey: ['userAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['storageAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['accessInvitations'] });
            clearStoragesCache(queryClient, variables?.storageId);
        },
        onError: (error) => {
            logger.error('Failed to respond to access invitation:', error, 'useAccess');
        }
    });

    const deleteInvitationMutation = useMutation({
        mutationFn: deleteAccessInvitationDoc,
        onSuccess: (_, variables) => {
            trackDelete('access_invitation');
            logger.info('Access invitation deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['userAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['storageAccessInvitations'] });
            queryClient.invalidateQueries({ queryKey: ['accessInvitations'] });
            clearStoragesCache(queryClient, variables?.storageId);
            if (variables?.storageId && getTargetStorageDocId() === variables.storageId) clearTargetStorage();
        },
        onError: (error) => {
            logger.error('Failed to delete access invitation:', error, 'useAccess');
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Backward compatible functions
    const reloadAccessDocs = () => {
        if (storageId) {
            refetchStorageInvitations();
        } else {
            refetchUserInvitations();
        }
    };

    // Get user's role based on their access
    const getUserRole: AccessRole | null = useMemo(() => {
        const userId = getUserId();
        if (!userId) {
            return null;
        }
        // Find the user's access invitation
        const userAccess = storageInvitations?.find(
            (invitation: AccessInvitationRead) => invitation.userId === userId
        );
        return userAccess ? userAccess.role : null;
    }, [storageInvitations, getUserId]);

    // Check if user has specific roles
    const limitToRoles = useMemo(() => {
        return (roles: AccessRole[]): boolean => {
            const userRole = getUserRole;
            if (!userRole) {
                return false;
            }
            return roles.includes(userRole);
        };
    }, [getUserRole]);

    return {
        // Access invitations
        storageInvitations: storageInvitations || [],
        filteredStorageInvitations,
        storageInvitationsStatus: getStatus(storageInvitationsLoading, storageInvitationsError),

        userInvitations: userInvitations || [],
        filteredUserInvitations,
        userInvitationsStatus: getStatus(userInvitationsLoading, userInvitationsError),

        // Backward compatibility aliases
        accessInvitations: storageId ? filteredStorageInvitations : filteredUserInvitations,
        filteredAccessInvitations: storageId ? filteredStorageInvitations : filteredUserInvitations,
        accessStatus: storageId ? getStatus(storageInvitationsLoading, storageInvitationsError) : getStatus(userInvitationsLoading, userInvitationsError),

        // Reload access documents
        reloadAccessDocs,
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        
        // User role utilities
        getUserRole,
        limitToRoles,

        // Mutations
        createAccessInvitation: createAccessInvitationMutation.mutateAsync,
        respondToInvitation: respondToInvitationMutation.mutateAsync,
        deleteInvitation: deleteInvitationMutation.mutateAsync,
        isCreating: createAccessInvitationMutation.isPending,
        isResponding: respondToInvitationMutation.isPending,
        isDeleting: deleteInvitationMutation.isPending,

        // Refresh functions
        refreshStorageInvitations: refetchStorageInvitations,
        refreshUserInvitations: refetchUserInvitations,
    };
};

export default useAccess

